"use client";

import { useState, useTransition } from "react";
import Link from "@/components/LocalizedLink";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Mail, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { SofiaConnectLogo } from "@/components/SofiaConnectLogo";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";

export function ForgotPasswordClient() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    startTransition(async () => {
      let supabase;
      try {
        supabase = createSupabaseBrowserClient();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Supabase not configured");
        return;
      }
      const redirectTo = `${window.location.origin}/portal/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) {
        setError(error.message);
        return;
      }
      setSent(true);
    });
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(74,158,245,0.08) 0%, transparent 60%), var(--background)",
        paddingTop: "84px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-8 justify-center">
          <SofiaConnectLogo className="w-10 h-10 text-[#4A9EF5]" aria-hidden />
          <div>
            <div className="text-lg font-semibold text-text-primary">Sofia Connect</div>
            <div className="text-[10px] font-mono tracking-widest uppercase text-text-muted">
              Client Portal
            </div>
          </div>
        </div>

        <div
          className="rounded-2xl p-8"
          style={{
            background: "var(--card-bg)",
            border: "1px solid var(--divider)",
            backdropFilter: "blur(16px)",
          }}
        >
          <h1 className="text-xl font-semibold text-text-primary mb-1">Forgot your password?</h1>
          <p className="text-sm text-text-muted mb-6">
            Enter your email and we&apos;ll send you a secure link to reset it.
          </p>

          {error && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg mb-4 text-sm"
              style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5" }}
              role="alert"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {sent ? (
            <div
              className="flex items-start gap-2 p-4 rounded-lg text-sm"
              style={{ background: "rgba(0,212,170,0.1)", border: "1px solid rgba(0,212,170,0.3)", color: "#5eead4" }}
              role="status"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium mb-1">Reset link sent</div>
                <div className="text-xs opacity-90">
                  If an account exists for <strong>{email}</strong>, you&apos;ll receive an email with a password reset link within a minute.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-text-muted mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg text-sm text-text-primary placeholder:text-text-muted outline-none"
                    style={{
                      background: "var(--hover-bg)",
                      border: "1px solid var(--divider)",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(74,158,245,0.5)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "var(--divider)")}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#4A9EF5", color: "#000" }}
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Send reset link
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t text-sm text-center" style={{ borderColor: "var(--divider)" }}>
            <Link
              href="/portal/login"
              className="inline-flex items-center gap-1.5 text-text-muted hover:text-text-secondary transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
